/**
 * API de profesionales (médicos, odontólogos, QFs) — tenant-scoped.
 */
import { apiFetch } from "./client";

// Igual que en `auth.ts`: las subidas con FormData no pasan por `apiFetch`
// —fetch tiene que poner él el boundary del multipart— así que necesitan la
// base de la API a mano.
const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "/api") as string;

export type TipoColegio =
  | "CMP" // Colegio Médico del Perú
  | "COP" // Colegio Odontológico del Perú
  | "CQFP" // Colegio Químico Farmacéutico del Perú
  | "CEP" // Colegio de Enfermeros del Perú
  | "CObP" // Colegio de Obstetras del Perú
  | "CTMP" // Colegio Tecnólogo Médico del Perú
  | "CPP" // Colegio de Psicólogos del Perú
  | "CNP" // Colegio de Nutricionistas del Perú
  | "CBP"; // Colegio de Biólogos del Perú
export type Sexo = "M" | "F" | "O";

export interface EspecialidadMini {
  id: number;
  codigo: string;
  nombre: string;
  principal?: boolean;
  // Colegios/profesiones que ejercen esta especialidad (CMP, COP, CQFP, ...).
  profesiones?: TipoColegio[];
  // Tarifa de la consulta en este policlínico (S/), si está configurada.
  precio?: string | null;
}

export interface SedeMini {
  id: number;
  nombre: string;
  principal?: boolean;
}

export interface UsuarioMini {
  id: number;
  email: string;
  activo: boolean;
  ultimo_login: string | null;
  rol_ids: number[];
}

export interface Profesional {
  id: number;
  persona_id: number;
  tipo_documento: string;
  num_documento: string;
  nombres: string;
  ap_paterno: string;
  ap_materno: string | null;
  fecha_nacimiento: string | null;
  sexo: Sexo | null;
  email: string | null;
  telefono: string | null;
  nombre_completo: string;
  tipo_colegio: TipoColegio | string;
  colegiatura: string;
  rne: string | null;
  firma_digital_activa: boolean;
  activo: boolean;
  created_at: string;
  especialidades: EspecialidadMini[];
  sedes: SedeMini[];
  usuario: UsuarioMini | null;
}

export interface CrearCuentaProfesional {
  email: string;
  password: string;
  rol_ids: number[];
}

export interface ProfesionalCreate {
  tipo_documento: "DNI" | "CE" | "PAS";
  num_documento: string;
  nombres: string;
  ap_paterno: string;
  ap_materno?: string | null;
  fecha_nacimiento?: string | null;
  sexo?: Sexo | null;
  email?: string | null;
  telefono?: string | null;
  tipo_colegio: TipoColegio;
  colegiatura: string;
  rne?: string | null;
  especialidad_codigos: string[];
  especialidad_principal?: string | null;
  sede_ids: number[];
  sede_principal_id?: number | null;
}

export interface ProfesionalUpdate {
  nombres?: string;
  ap_paterno?: string;
  ap_materno?: string | null;
  email?: string | null;
  telefono?: string | null;
  rne?: string | null;
  firma_digital_activa?: boolean;
  activo?: boolean;
  especialidad_codigos?: string[];
  especialidad_principal?: string | null;
  sede_ids?: number[];
  sede_principal_id?: number | null;
}

export function listProfesionales(
  search?: string,
  soloActivos = false,
  limit = 200,
): Promise<Profesional[]> {
  const params = new URLSearchParams();
  if (search) params.set("q", search);
  if (soloActivos) params.set("solo_activos", "true");
  params.set("limit", String(limit));
  return apiFetch<Profesional[]>(`/v1/profesionales?${params.toString()}`);
}

export function getProfesional(id: number): Promise<Profesional> {
  return apiFetch<Profesional>(`/v1/profesionales/${id}`);
}

export function createProfesional(payload: ProfesionalCreate): Promise<Profesional> {
  return apiFetch<Profesional>("/v1/profesionales", {
    method: "POST",
    body: payload,
  });
}

export function updateProfesional(
  id: number,
  payload: ProfesionalUpdate,
): Promise<Profesional> {
  return apiFetch<Profesional>(`/v1/profesionales/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

export function listEspecialidades(): Promise<EspecialidadMini[]> {
  return apiFetch<EspecialidadMini[]>("/v1/profesionales/especialidades");
}

export function crearCuentaProfesional(
  profId: number,
  payload: CrearCuentaProfesional,
): Promise<Profesional> {
  return apiFetch<Profesional>(`/v1/profesionales/${profId}/crear_usuario`, {
    method: "POST",
    body: payload,
  });
}

/** Lista los roles disponibles para el tenant del usuario actual. */
export function listMyTenantRoles(): Promise<
  Array<{ id: number; codigo: string; nombre: string; tenant_id: number | null }>
> {
  return apiFetch("/v1/roles");
}

// ------------------------------------------------------------ la firma ----

/**
 * La firma del profesional que está dentro. Se carga UNA vez, no por receta.
 *
 * Sin `profesional_id` en la ruta a propósito: con un id por parámetro,
 * cualquiera con permiso de editar profesionales podría cargarle una firma a
 * otro médico, y esa firma saldría estampada en recetas que él no escribió.
 */
export async function subirMiFirma(file: File): Promise<void> {
  // FormData no pasa por apiFetch: fetch tiene que poner él mismo el
  // Content-Type con el boundary del multipart.
  const { getTokens } = await import("../auth/tokens");
  const tokens = getTokens();
  const form = new FormData();
  form.append("file", file);
  const response = await fetch(`${API_BASE}/v1/profesionales/me/firma`, {
    method: "PUT",
    headers: tokens?.access_token
      ? { Authorization: `Bearer ${tokens.access_token}` }
      : {},
    body: form,
  });
  if (!response.ok) {
    const cuerpo = await response.json().catch(() => null);
    throw new Error(cuerpo?.detail ?? "No se pudo subir la firma.");
  }
}

export function borrarMiFirma(): Promise<void> {
  return apiFetch<void>("/v1/profesionales/me/firma", { method: "DELETE" });
}

/**
 * La imagen de la firma, ya descargada como blob.
 *
 * No se puede poner la URL en un `<img src>`: el endpoint pide el token en la
 * cabecera y el navegador no lo manda en una etiqueta de imagen.
 */
export async function miFirmaBlob(): Promise<string | null> {
  const { getTokens } = await import("../auth/tokens");
  const tokens = getTokens();
  const response = await fetch(`${API_BASE}/v1/profesionales/me/firma`, {
    headers: tokens?.access_token
      ? { Authorization: `Bearer ${tokens.access_token}` }
      : {},
  });
  if (!response.ok) return null;
  return URL.createObjectURL(await response.blob());
}
